import { Button } from '@artic/ui/button';
import { Input } from '@artic/ui/input';
import { Label } from '@artic/ui/label';
import { Skeleton } from '@artic/ui/skeleton';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { usePartnerId, usePartnerName } from '../../features/inbox/use-partner-name';
import {
  calculateComplexityPreview,
  generateProductCodePreview,
} from '../../features/products/product-code';
import { partnerContainersQueryOptions, useCreateProduct } from '../../features/products/queries';
import type { PartnerContainerOption } from '../../features/products/types';

export const Route = createFileRoute('/products/new')({
  staticData: { breadcrumb: 'New Product' },
  component: NewProductView,
});

function NewProductView() {
  const partnerId = usePartnerId();
  const partnerName = usePartnerName();
  const navigate = useNavigate();

  const {
    data: containers,
    isLoading,
    isError,
  } = useQuery(partnerContainersQueryOptions(partnerId));

  const createMutation = useCreateProduct(partnerId);

  const [productName, setProductName] = useState('');
  const [productDescription, setProductDescription] = useState('');
  // Map of container_id → selected option
  const [selections, setSelections] = useState(() => new Map<string, PartnerContainerOption>());

  const activeContainers =
    containers?.filter((c) => c.is_active).sort((a, b) => a.display_order - b.display_order) ?? [];

  // Only consider selections for currently active containers (filters stale entries)
  const activeContainerIds = new Set(activeContainers.map((c) => c.container_id));

  const allContainersSelected =
    activeContainers.length > 0 && activeContainers.every((c) => selections.has(c.container_id));

  const selectedOptions = Array.from(selections.values());

  const productCodePreview =
    selectedOptions.length > 0
      ? generateProductCodePreview(
          selectedOptions.map((opt) => {
            const container = activeContainers.find((c) => c.container_id === opt.container_id);
            return {
              option_code: opt.option_code,
              container_sort_order: container?.container_sort_order ?? 0,
            };
          }),
        )
      : '—';

  const complexityPreview =
    selectedOptions.length > 0 ? calculateComplexityPreview(selectedOptions) : 0;

  const handleSelectOption = (containerId: string, option: PartnerContainerOption) => {
    setSelections((prev) => {
      const next = new Map(prev);
      next.set(containerId, option);
      return next;
    });
  };

  const handleSubmit = () => {
    if (!partnerName || !productName.trim() || !allContainersSelected) return;

    createMutation.mutate(
      {
        partner_name: partnerName,
        product_name: productName.trim(),
        product_description: productDescription.trim() || null,
        container_selections: Array.from(selections.entries())
          .filter(([containerId]) => activeContainerIds.has(containerId))
          .map(([containerId, option]) => ({
            container_id: containerId,
            container_option_id: option.container_option_id,
          })),
      },
      {
        onSuccess: () => {
          void navigate({ to: '/products' });
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-xl font-semibold">New Product</h1>
        <div className="flex flex-col gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-destructive py-10 text-center text-sm">
        Failed to load container configuration. Please try again.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-xl font-semibold">New Product</h1>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Form */}
        <div className="flex flex-col gap-4 lg:col-span-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="product-name">Product Name</Label>
            <Input
              id="product-name"
              placeholder="Enter product name"
              value={productName}
              onChange={(e) => {
                setProductName(e.target.value);
              }}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="product-description">
              Description <span className="text-muted-foreground">(optional)</span>
            </Label>
            <textarea
              id="product-description"
              placeholder="Enter description"
              value={productDescription}
              onChange={(e) => {
                setProductDescription(e.target.value);
              }}
              rows={2}
              className="border-input bg-background placeholder:text-muted-foreground focus-visible:ring-ring flex min-h-[60px] w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-1 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          {activeContainers.length === 0 ? (
            <div className="text-muted-foreground rounded-md border p-4 text-sm">
              No containers configured for this partner. Contact an admin to activate containers.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <Label>Container Selections</Label>
              {activeContainers.map((container) => {
                const activeOptions = container.options.filter((o) => o.is_active_for_partner);
                const selected = selections.get(container.container_id);

                return (
                  <div key={container.container_id} className="flex items-center gap-3">
                    <span className="w-36 shrink-0 text-sm font-medium">
                      {container.container_name}
                    </span>
                    <select
                      value={selected?.container_option_id ?? ''}
                      onChange={(e) => {
                        const option = activeOptions.find(
                          (o) => o.container_option_id === e.target.value,
                        );
                        if (option) {
                          handleSelectOption(container.container_id, option);
                        }
                      }}
                      className="border-input bg-background focus-visible:ring-ring w-full max-w-xs rounded-md border px-3 py-2 text-sm focus-visible:ring-1 focus-visible:outline-none"
                    >
                      <option value="">Select...</option>
                      {activeOptions.map((option) => (
                        <option key={option.container_option_id} value={option.container_option_id}>
                          {option.option_name} ({option.option_code})
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Preview panel */}
        <div className="rounded-md border p-4">
          <h2 className="mb-3 text-sm font-semibold">Preview</h2>
          <div className="flex flex-col gap-2 text-sm">
            <div>
              <span className="text-muted-foreground">Product Code:</span>{' '}
              <span className="font-mono font-medium">{productCodePreview}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Complexity Score:</span>{' '}
              <span className="font-medium">{complexityPreview > 0 ? complexityPreview : '—'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          onClick={handleSubmit}
          disabled={createMutation.isPending || !productName.trim() || !allContainersSelected}
        >
          {createMutation.isPending ? 'Creating...' : 'Create Product'}
        </Button>
        <Button variant="outline" asChild>
          <Link to="/products">Cancel</Link>
        </Button>
      </div>

      {createMutation.isError && (
        <div className="text-destructive text-sm">
          {createMutation.error instanceof Error
            ? createMutation.error.message
            : 'Failed to create product. Please try again.'}
        </div>
      )}
    </div>
  );
}
